import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { AbstractDynamicComponent } from '@components/abstracts';
import { ListingPackProduits } from '@components/listing-pack-produits/models';
import { imageMock } from '@mock/schema/components/shared';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterToggleGroupComponent } from '@shared/components/filter-toggle-group';
import { GridSkeletonComponent } from '@shared/components/grid-skeleton';
import { ImageComponent } from '@shared/components/image';
import { SvgComponent } from '@shared/components/svg';
import { LabelKey } from '@shared/models';
import { ReplaceWholeWordPipe } from '@shared/pipe';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { Pack } from '@shared/services/elastic-search/models';
import { LogsService } from '@shared/services/logs';
import { ResponsiveService } from '@shared/services/responsive';
import { UrlParamsService } from '@shared/services/url-params';
import { take, tap } from 'rxjs';

@Component({
  selector: 'app-listing-pack-produits',
  imports: [
    FilterToggleGroupComponent,
    GridSkeletonComponent,
    NgTemplateOutlet,
    ImageComponent,
    SvgComponent,
    ReplaceWholeWordPipe,
    TranslatePipe,
  ],
  templateUrl: './listing-pack-produits.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListingPackProduitsComponent extends AbstractDynamicComponent<ListingPackProduits> implements OnInit {
  protected readonly filters = signal<LabelKey[]>(null!);
  protected readonly packs = signal<Pack[] | null>(null);
  protected readonly filterName = 'filter_pack_produit';
  protected readonly imageMock = imageMock;
  protected readonly ImageComponent = ImageComponent;
  protected readonly breakpoints = inject(ResponsiveService).breakpoints;
  protected displayRightButton = true;
  protected displayLeftButton = false;
  private readonly filtersUrlParam = inject(UrlParamsService).param(this.filterName);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    effect(() => {
      const { productPackType } = this.data;
      // sinkAndTap shows a required filter toggle: don't fetch until the filter is in the URL,
      // so we never run an unfiltered query that gets replaced once the default is applied.
      // Other pack types have no filter toggle, so they fetch directly.
      const selectedFilter = this.filtersUrlParam();
      if (productPackType === 'sinkAndTap' && !selectedFilter) return;
      const filters = selectedFilter?.split('__');
      this.elasticSearchService
        .getPacks(productPackType, filters)
        .pipe(
          take(1),
          tap((packs) => this.packs.set(packs))
        )
        .subscribe({ error: (err) => this.logs.error('ListingPackProduitsComponent', 'getPacks failed', err) });
    });
  }

  ngOnInit(): void {
    const { productPackType } = this.data;
    if (productPackType === 'sinkAndTap') {
      this.elasticSearchService
        .getSinkTapPacksFilter()
        .pipe(
          take(1),
          tap((filters) => this.filters.set(filters))
        )
        .subscribe({
          error: (err) => this.logs.error('ListingPackProduitsComponent', 'getSinkTapPacksFilter failed', err),
        });
    }
  }

  moveSlider(direction: 'prev' | 'next', scrollElement: HTMLElement): void {
    const maxWidth = scrollElement.clientWidth;
    const scrollWidth = scrollElement.children[0]!.clientWidth;

    if (direction === 'prev') {
      this.displayRightButton = true;
      if (scrollElement.scrollLeft - scrollWidth <= 0) this.displayLeftButton = false;
    } else {
      this.displayLeftButton = true;
      if (scrollElement.scrollLeft + scrollWidth >= maxWidth) this.displayRightButton = false;
    }

    scrollElement.scrollBy({
      left: scrollWidth * (direction === 'prev' ? -1 : 1),
      behavior: 'smooth',
    });
  }
}
